// Global variables for dynamic server configuration
let globalServerName = '';
let globalSystemID = '';

// Function to parse query parameters and set global variables
const initializeServerConfig = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Extract ServerName and SystemName from query parameters
  const serverNameParam = urlParams.get('ServerName') || urlParams.get('servername');
  const systemNameParam = urlParams.get('SystemName') || urlParams.get('systemname') || urlParams.get('SystemID') || urlParams.get('systemid');
  
  // Legacy field names sometimes included in POST bodies (backend reads from Postgres; these are optional)
  globalServerName = serverNameParam || 'STAGESERVERDS';
  globalSystemID = systemNameParam || 'tsg';
};

// Initialize on module load
initializeServerConfig();

// Export functions to get current server configuration
export const getServerName = () => globalServerName;
export const getSystemID = () => globalSystemID;

// Export function to reinitialize if needed
export const reinitializeServerConfig = () => {
  initializeServerConfig();
};

/** Pathnames for Profile Safe features — all are implemented on the PaySafe backend (Postgres), not Azure REX */
export const API_CONFIG = {
  PROFILE_SAFE_ENDPOINTS: {
    GET_PROFILE: '/api/GetProfile',
    GET_PROFILE_DETAILS: '/api/GetProfileDetails',
    GET_CONFIG_DETAILS: '/api/GetRXProfileConfigDetails',
    GET_FILE_DATA: '/api/GetFileData',
    GET_RX_PROFILES_SUMMARY: '/api/GetProfilesSummary',
    GET_TOUCH_POINTS_MASTER_DATA: '/api/GetTouchPointsMasterData',
    GET_PATRON: '/api/GetPatron',
    GET_PROFILE_FAILED_TRANSACTIONS: '/api/GetProfileFailedTransactions',
    GET_PROFILE_SUCCESS_TRANSACTIONS: '/api/GetProfileSuccessTransactions',
    GET_CLIENT_TRANSACTIONS: '/api/GetClientTransactions'
  },
  LOCAL_API: {
    // Fallback for unknown hosts (prefer canonical staging API, not legacy paysafeapi)
    BASE_URL: 'https://paysafego.appstaging.biz',
    ENDPOINTS: {
      RULES: '/api/rules',
      CASES: '/api/cases',
      STATS: '/api/stats',
      PROCESS: '/api/process',
      HEALTH: '/health'
    }
  }
};

/** True if URL should never be used as API base (legacy hosts — use PaySafe /api on paysafego or same origin). */
function isBlockedExternalApiBase(u) {
  if (typeof u !== 'string') return false
  if (/rexapi\.azurewebsites\.net/i.test(u)) return true
  // Never point ?apiBase= at legacy paysafeapi — it does not serve psg2 routes
  if (/paysafeapi\.appstaging\.biz/i.test(u)) return true
  return false
}

/** True when the document is served from the legacy API hostname (relative /api would 404 there). */
function isLegacyPaysafeApiHost() {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'paysafeapi.appstaging.biz' || h.endsWith('.paysafeapi.appstaging.biz')
}

/**
 * Build-time API origin (Vite: VITE_API_ORIGIN). When set, every /api URL is absolute to this host —
 * fixes stale bundles, alternate hostnames, and SPAs still served from paysafeapi.* while nginx/api live on paysafego.
 * Tenant / prod builds: leave unset so same-origin relative /api is used.
 */
function buildTimeApiOrigin() {
  try {
    const o = import.meta.env?.VITE_API_ORIGIN
    return o ? String(o).trim().replace(/\/$/, '') : ''
  } catch {
    return ''
  }
}

/**
 * VITE_API_ORIGIN is often the shared gateway (paysafego.appstaging.biz). When the SPA is served from
 * another host (e.g. tsg.appstaging.biz) nginx proxies /api on that host too — absolute URLs must not
 * cross back to the gateway or login hits the wrong tenant context.
 */
function resolvedBakedApiOrigin() {
  const baked = buildTimeApiOrigin()
  if (!baked || typeof window === 'undefined') return baked
  try {
    const u = new URL(baked.includes('://') ? baked : `https://${baked}`)
    if (window.location.hostname.toLowerCase() !== u.hostname.toLowerCase()) return ''
  } catch {
    return baked
  }
  return baked
}

function stagingCanonicalApiOrigin() {
  if (typeof window === 'undefined') return ''
  if (!isLegacyPaysafeApiHost()) return ''
  const env = typeof import.meta !== 'undefined' && import.meta.env?.VITE_STAGING_API_ORIGIN
  return (env && String(env).trim()) || 'https://paysafego.appstaging.biz'
}

/**
 * Use relative `/api/...` URLs in the browser so every host (paysafego.appstaging.biz, tsg.paysafego.net,
 * www.*, IP via Host header, etc.) hits the API behind nginx on the same origin.
 * Exception: UI loaded on paysafeapi.appstaging.biz must call the real API host (see stagingCanonicalApiOrigin).
 * Local dev: Vite proxies `/api` → backend.
 */
function useRelativeApiPaths() {
  if (typeof window === 'undefined') return false
  if (stagingCanonicalApiOrigin()) return false
  return true
}

export const getApiUrl = (endpoint) => {
  const path = API_CONFIG.PROFILE_SAFE_ENDPOINTS[endpoint];
  if (!path) throw new Error(`Unknown profile endpoint key: ${endpoint}`);
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const baked = typeof window !== 'undefined' ? resolvedBakedApiOrigin() : ''
  if (baked) return `${baked}${normalized}`
  if (typeof window !== 'undefined' && isLegacyPaysafeApiHost()) {
    const base = stagingCanonicalApiOrigin().replace(/\/$/, '')
    return `${base}${normalized}`
  }
  if (useRelativeApiPaths()) return normalized;
  const baseUrl = getCurrentApiBase();
  return `${baseUrl}${normalized}`;
};

// Determine API base from the current frontend hostname
function detectApiBaseFromHost() {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'http://127.0.0.1:4400';
  // Legacy hostname: SPA may still be linked here; API routes live on paysafego staging (CORS allows this origin)
  const stagingApi = stagingCanonicalApiOrigin()
  if (stagingApi) return stagingApi
  // UI + API on one host (nginx /api → PM2). Use endsWith so www. and subdomains match.
  if (host.endsWith('paysafego.appstaging.biz')) return window.location.origin;
  // Other *.appstaging.biz apps (nginx UI + /api on same origin) — do not force legacy paysafeapi host
  if (host.endsWith('appstaging.biz')) return window.location.origin;
  // paysafego.net: tenant apps (tsg.paysafego.net, etc.) use same host for UI + /api behind nginx
  if (host.endsWith('paysafego.net')) {
    if (host === 'api.paysafego.net') return 'https://api.paysafego.net';
    return window.location.origin;
  }
  return API_CONFIG.LOCAL_API.BASE_URL; // fallback
}

export const getCurrentApiBase = () => {
  const baked = typeof window !== 'undefined' ? resolvedBakedApiOrigin() : ''
  if (baked) return baked
  const params = new URLSearchParams(window.location.search);
  const override = params.get('apiBase');
  const forceLocal = params.get('useLocalApi') === '1' || params.get('localApi') === '1';
  if (forceLocal) return 'http://127.0.0.1:4400';
  if (override && !isBlockedExternalApiBase(override)) return override.trim();
  return detectApiBaseFromHost();
};

/**
 * On gateway hosts (shared URL like paysafego.appstaging.biz), append ?tenant= to every /api/* request.
 * Backend accepts tenant via query or X-Tenant; some proxies strip custom headers — query is reliable.
 * Uses getEffectiveUrlTenantSlug() (URL, sessionStorage, localStorage, or env).
 */
function appendTenantQueryForGatewayApi(resolvedUrl) {
  if (typeof window === 'undefined' || !resolvedUrl) return resolvedUrl
  if (!isTenantQueryGatewayHost() && !isLocalDevHostname()) return resolvedUrl
  const slug = getEffectiveUrlTenantSlug()
  if (!slug) return resolvedUrl
  const path = getUrlPathname(resolvedUrl)
  if (!path.startsWith('/api')) return resolvedUrl
  try {
    const full =
      resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://')
        ? resolvedUrl
        : new URL(resolvedUrl, window.location.origin).toString()
    const u = new URL(full)
    const existing = u.searchParams.get('tenant') || u.searchParams.get('t')
    if (existing && existing.toLowerCase() === slug) return resolvedUrl
    u.searchParams.set('tenant', slug)
    if (resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://')) return u.toString()
    return `${u.pathname}${u.search}`
  } catch {
    return resolvedUrl
  }
}

/** Rewrite fetch URL so gateway tenant is always in the query (not only /api/login). */
function withGatewayTenantOnApiUrl(input) {
  if (typeof input !== 'string' || !input) return input
  const apiBase = getCurrentApiBase()
  if (!isPaySafeApiRequestUrl(input, apiBase)) return input
  return appendTenantQueryForGatewayApi(input)
}

export const getLocalApiUrl = (endpoint = '') => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const baked = typeof window !== 'undefined' ? resolvedBakedApiOrigin() : ''
  let out
  if (baked) out = `${baked}${path}`
  else if (typeof window !== 'undefined' && isLegacyPaysafeApiHost()) {
    const base = stagingCanonicalApiOrigin().replace(/\/$/, '')
    out = `${base}${path}`
  } else if (useRelativeApiPaths()) out = path
  else out = `${getCurrentApiBase()}${path}`
  return appendTenantQueryForGatewayApi(out)
};

// Get JWT for API requests
function getAuthToken() {
  if (typeof window === 'undefined') return ''
  try {
    const userStr = localStorage.getItem('paysafego_user')
    return userStr ? (JSON.parse(userStr).token || '') : ''
  } catch { return '' }
}

/** Pathname only — supports absolute and relative URLs */
function getUrlPathname(url) {
  if (!url || typeof url !== 'string') return ''
  const noHash = url.split('#')[0]
  const noQuery = noHash.split('?')[0]
  if (noQuery.startsWith('http://') || noQuery.startsWith('https://')) {
    try {
      return new URL(noQuery).pathname || ''
    } catch {
      return ''
    }
  }
  return noQuery
}

/** Never attach Bearer to login — stale JWT makes the API JWT hook return 401 before credentials are checked */
function isLoginRequestUrl(url) {
  const p = getUrlPathname(url)
  return p === '/api/login' || p.endsWith('/api/login')
}

function isPaySafeApiRequestUrl(url, apiBase) {
  if (!url || typeof url !== 'string') return false
  if (url.startsWith(apiBase)) return true
  // Relative /api/* (same-origin after deploy) must still get JWT
  if (url.startsWith('/api')) return true
  return false
}

/** sessionStorage: tenant from ?tenant= for this tab */
const URL_TENANT_SESSION_KEY = 'paysafego_url_tenant_slug'
/** localStorage: survives tab close — drives ?tenant= on all /api/* calls on the gateway host */
const URL_TENANT_LOCAL_KEY = 'paysafego_gateway_tenant_slug'

function getTenantQueryGatewayHosts() {
  try {
    const raw = import.meta.env?.VITE_TENANT_QUERY_GATEWAY_HOSTS
    const s = raw != null && String(raw).trim() ? String(raw).trim() : 'paysafego.appstaging.biz'
    return s.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean)
  } catch {
    return ['paysafego.appstaging.biz']
  }
}

/** Central API host (no tenant in subdomain). Same as backend CENTRAL_API_TENANT_QUERY_HOSTS — needs ?tenant= / X-Tenant. */
function isCentralApiTenantHost() {
  if (typeof window === 'undefined') return false
  return window.location.hostname.toLowerCase() === 'api.paysafego.net'
}

function isTenantQueryGatewayHost() {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.toLowerCase()
  if (isCentralApiTenantHost()) return true
  return getTenantQueryGatewayHosts().some((allowed) => h === allowed)
}

/** Backend accepts ?tenant= / X-Tenant on localhost; MULTI_TENANT_ENABLED=true skips DEFAULT_TENANT there, so the SPA must send a slug. */
function isLocalDevHostname() {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.toLowerCase()
  return h === 'localhost' || h === '127.0.0.1'
}

function sanitizeUrlTenantSlug(raw) {
  const t = String(raw).trim().toLowerCase()
  if (!t || t.length > 64 || !/^[a-z0-9_-]+$/.test(t)) return null
  return t
}

function persistGatewayTenantSlug(s) {
  try {
    sessionStorage.setItem(URL_TENANT_SESSION_KEY, s)
  } catch { /* ignore */ }
  try {
    localStorage.setItem(URL_TENANT_LOCAL_KEY, s)
  } catch { /* ignore */ }
}

/**
 * When the SPA is served from a gateway host (e.g. paysafego.appstaging.biz), ?tenant=tsg (or ?t=tsg)
 * selects the org. Stored in sessionStorage + localStorage so /login and fetch('/api/login') still work
 * after navigation or a new tab. Must match backend QUERY_TENANT_HOSTS.
 */
export function getEffectiveUrlTenantSlug() {
  if (typeof window === 'undefined') return null
  if (!isTenantQueryGatewayHost() && !isLocalDevHostname()) return null
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('tenant') ?? params.get('t')
    if (raw != null && String(raw).trim() !== '') {
      const s = sanitizeUrlTenantSlug(raw)
      if (s) {
        persistGatewayTenantSlug(s)
        return s
      }
    }
  } catch { /* ignore */ }
  let fromSession = null
  try {
    fromSession = sessionStorage.getItem(URL_TENANT_SESSION_KEY)
  } catch { /* ignore */ }
  if (fromSession) {
    const stored = sanitizeUrlTenantSlug(fromSession)
    if (stored) return stored
  }
  let fromLocal = null
  try {
    fromLocal = localStorage.getItem(URL_TENANT_LOCAL_KEY)
  } catch { /* ignore */ }
  if (fromLocal) {
    const stored = sanitizeUrlTenantSlug(fromLocal)
    if (stored) return stored
  }
  try {
    const localDef = import.meta.env?.VITE_DEFAULT_LOCAL_TENANT
    if (isLocalDevHostname() && localDef != null && String(localDef).trim()) {
      const s = sanitizeUrlTenantSlug(String(localDef))
      if (s) return s
    }
  } catch { /* ignore */ }
  try {
    const envDef = import.meta.env?.VITE_DEFAULT_GATEWAY_TENANT
    if (envDef != null && String(envDef).trim()) {
      const s = sanitizeUrlTenantSlug(String(envDef))
      if (s) return s
    }
  } catch { /* ignore */ }
  return null
}

/** Aligned with backend RESERVED_SUBDOMAINS — not tenant GSIDs */
const RESERVED_TENANT_SUBDOMAINS = new Set([
  'www', 'api', 'admin', 'mail', 'app', 'cdn', 'static', 'test', 'dev', 'staging', 'assets', 'auth', 'login',
])
/** Staging hosts where the first label is the shared gateway, not a tenant */
const APPSTAGING_GATEWAY_SUBDOMAINS = new Set([
  'paysafego', 'paysafego-platform', 'paysafeapi',
])

/**
 * When the SPA is served from a tenant host (e.g. tsg.paysafego.net, tsg.appstaging.biz), the org
 * is in the hostname. Used so cross-origin calls to https://api.paysafego.net still send X-Tenant
 * (getEffectiveUrlTenantSlug() is null on non-gateway hosts).
 */
function tenantSlugFromTenantSubdomainHost() {
  if (typeof window === 'undefined') return null
  const h = window.location.hostname.toLowerCase()
  if (h.endsWith('.paysafego.net')) {
    if (h === 'paysafego.net' || h === 'www.paysafego.net') return null
    const sub = h.slice(0, -'.paysafego.net'.length)
    if (!sub || sub.includes('.')) return null
    if (RESERVED_TENANT_SUBDOMAINS.has(sub)) return null
    return sanitizeUrlTenantSlug(sub)
  }
  if (h.endsWith('.appstaging.biz')) {
    const sub = h.slice(0, -'.appstaging.biz'.length)
    if (!sub || sub.includes('.')) return null
    if (APPSTAGING_GATEWAY_SUBDOMAINS.has(sub)) return null
    return sanitizeUrlTenantSlug(sub)
  }
  return null
}

function attachPaySafeTenantHeader(headers, url) {
  const apiBase = getCurrentApiBase()
  if (!isPaySafeApiRequestUrl(url, apiBase)) return
  let slug = getEffectiveUrlTenantSlug()
  if (!slug) slug = tenantSlugFromTenantSubdomainHost()
  if (slug && !headers.has('X-Tenant')) headers.set('X-Tenant', slug)
}

// Ensure requests to our local API include the JWT
function ensureAuthHeaders(input, init = {}) {
  const url = typeof input === 'string' ? input : (input?.url || '')
  if (isLoginRequestUrl(url)) {
    const headers = new Headers(init.headers || {})
    headers.delete('Authorization')
    attachPaySafeTenantHeader(headers, url)
    return { ...init, headers }
  }
  const apiBase = getCurrentApiBase()
  if (!isPaySafeApiRequestUrl(url, apiBase)) return init
  const headers = new Headers(init.headers || {})
  const token = getAuthToken()
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
  attachPaySafeTenantHeader(headers, url)
  return { ...init, headers }
}

// Global helper to include Authorization header automatically
// On 401 from our API: clear stale token and redirect to login
export const authFetch = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : (input?.url || '')
  const headers = new Headers(init.headers || {})
  if (!isLoginRequestUrl(url)) {
    const token = getAuthToken()
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
  } else {
    headers.delete('Authorization')
  }
  attachPaySafeTenantHeader(headers, url)
  const res = await fetch(input, { ...init, headers })
  if (res.status === 401 && isPaySafeApiRequestUrl(url, getCurrentApiBase()) && !isLoginRequestUrl(url)) {
    localStorage.removeItem('paysafego_user')
    window.location.href = '/login?expired=1'
  }
  return res
}

// Patched fetch that adds JWT when calling our API (for any fetch used by linked pages)
const _originalFetch = typeof window !== 'undefined' ? window.fetch : null
if (_originalFetch && typeof window !== 'undefined') {
  window.fetch = function (input, init = {}) {
    const resolved =
      typeof input === 'string'
        ? withGatewayTenantOnApiUrl(input)
        : input instanceof Request && isPaySafeApiRequestUrl(input.url, getCurrentApiBase())
          ? (() => {
              const u = appendTenantQueryForGatewayApi(input.url)
              return u !== input.url ? new Request(u, input) : input
            })()
          : input
    const withAuth = ensureAuthHeaders(resolved, init)
    return _originalFetch.call(this, resolved, withAuth)
  }
}